import { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import moment from 'moment';
import PropTypes from 'prop-types';
import { TranslucentContainer } from '../../../../../components';
import { useIsMobile } from '../../../../../hooks/useIsMobile';

function RetailSpaceCalendarView({ retailSpaces }) {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const [currentMonth, setCurrentMonth] = useState(moment());

  // Helper function to convert 24-hour format to 12-hour format with AM/PM
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return '';
    // Parse the time string (e.g., "14:30" or "14:30:00")
    const time = moment(timeString, 'HH:mm:ss').isValid()
      ? moment(timeString, 'HH:mm:ss')
      : moment(timeString, 'HH:mm');
    // Return formatted time with AM/PM
    return time.isValid() ? time.format('h:mm A') : timeString;
  };

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  // Get all days for the current month view
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    const days = [];
    const day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  }, [currentMonth]);

  // Get retail spaces for a specific date
  const getRetailSpacesForDate = (date) => {
    const availableSlots = [];

    retailSpaces.forEach((profile) => {
      // Check if profile is active
      if (profile.status !== 'active') return;

      // Check if date falls within profile's date range
      const profileStartDate = profile.start_date ? moment(profile.start_date) : null;
      const profileEndDate = profile.end_date ? moment(profile.end_date) : null;

      if (profileStartDate && date.isBefore(profileStartDate, 'day')) return;
      if (profileEndDate && date.isAfter(profileEndDate, 'day')) return;

      // Get day of week for this date
      const dayOfWeek = date.format('dddd').toLowerCase(); // 'monday', 'tuesday', etc.

      // Check if there are schedules for this day of week
      if (profile.schedules && profile.schedules.length > 0) {
        profile.schedules.forEach((schedule) => {
          if (schedule.day_of_week === dayOfWeek && schedule.is_active) {
            availableSlots.push({
              id: `${profile.id}-${schedule.id}-${date.format('YYYY-MM-DD')}`,
              profileId: profile.id,
              scheduleId: schedule.id,
              date: date.format('YYYY-MM-DD'),
              name: `${formatTimeTo12Hour(schedule.start_time)} - ${formatTimeTo12Hour(
                schedule.end_time,
              )}`,
              startTime: schedule.start_time,
              endTime: schedule.end_time,
              price: schedule.price_per_hour,
              slotsAvailable: schedule.slots_available,
              spaceType: schedule.space_type,
            });
          }
        });
      }
    });

    return availableSlots;
  };
  // Check if date is in current month
  const isCurrentMonth = (date) => date.month() === currentMonth.month();

  // Check if date is in the past
  const isPastDate = (date) => date.isBefore(moment(), 'day');

  // Check if date is today
  const isToday = (date) => date.isSame(moment(), 'day');

  // Weekday headers
  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <Grid container justifyContent="center" marginTop={4}>
      <Grid item xs={12} lg={11}>
        <TranslucentContainer>
          <Box sx={{ p: isMobile ? 2 : 4 }}>
            {/* Calendar Header */}
            <Grid container alignItems="center" justifyContent="space-between" marginBottom={3}>
              <Grid item>
                <IconButton onClick={previousMonth} sx={{ color: '#EE7B57' }}>
                  <ChevronLeft fontSize="large" />
                </IconButton>
              </Grid>
              <Grid item>
                <Typography variant="h4" fontWeight="bold" color="#EE7B57">
                  {currentMonth.format('MMMM YYYY')}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={nextMonth} sx={{ color: '#EE7B57' }}>
                  <ChevronRight fontSize="large" />
                </IconButton>
              </Grid>
            </Grid>

            {/* Calendar Grid */}
            <Paper elevation={3} sx={{ p: 2, bgcolor: 'white' }}>
              {/* Weekday Headers */}
              <Grid container spacing={1} marginBottom={1}>
                {weekDays.map((day) => (
                  <Grid item xs={12 / 7} key={day}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      textAlign="center"
                      color="#666"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                      {isMobile ? day.slice(0, 3) : day}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar Days */}
              <Grid container spacing={1}>
                {calendarDays.map((day) => {
                  const spacesForDay = getRetailSpacesForDate(day);
                  const isCurrentMonthDay = isCurrentMonth(day);
                  const isPast = isPastDate(day);
                  const todayDate = isToday(day);

                  let bgColor = 'white';
                  if (todayDate) {
                    bgColor = 'rgba(238, 123, 87, 0.1)';
                  } else if (isPast) {
                    bgColor = '#F9F9F9';
                  }

                  let borderColor = '#E0E0E0';
                  if (todayDate) {
                    borderColor = '#EE7B57';
                  }

                  return (
                    <Grid item xs={12 / 7} key={day.format('YYYY-MM-DD')}>
                      <Box
                        sx={{
                          minHeight: isMobile ? '60px' : '100px',
                          border: `2px solid ${borderColor}`,
                          borderRadius: 1,
                          bgcolor: bgColor,
                          opacity: !isCurrentMonthDay ? 0.4 : 1,
                          p: 1,
                          cursor: !isPast && isCurrentMonthDay ? 'pointer' : 'default',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor:
                              !isPast && isCurrentMonthDay ? 'rgba(238, 123, 87, 0.05)' : bgColor,
                            transform: !isPast && isCurrentMonthDay ? 'translateY(-2px)' : 'none',
                          },
                        }}>
                        <Typography
                          variant="body2"
                          fontWeight={todayDate ? 'bold' : 'normal'}
                          color={isPast ? '#999' : '#333'}
                          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {day.format('D')}
                        </Typography>

                        {/* Retail space availability slots */}
                        {spacesForDay.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {spacesForDay.map((slot) => (
                              <Box
                                key={slot.id}
                                sx={{
                                  fontSize: '0.65rem',
                                  bgcolor: isPast ? '#BDBDBD' : '#3c99ba',
                                  color: 'white',
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  mb: 0.25,
                                }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    fontSize: isMobile ? '0.55rem' : '0.65rem',
                                    fontWeight: 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                  {slot.name}
                                </Typography>
                                {!isMobile && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      fontSize: '0.6rem',
                                      opacity: 0.9,
                                    }}>
                                    ${slot.price}/hr • {slot.slotsAvailable} slots
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Empty State Message */}
            {retailSpaces.length === 0 && (
              <Box textAlign="center" marginTop={4} py={4}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Retail Spaces Created Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your first retail space using the &quot;Create Retail Space&quot; tab
                </Typography>
              </Box>
            )}
          </Box>
        </TranslucentContainer>
      </Grid>
    </Grid>
  );
}

RetailSpaceCalendarView.propTypes = {
  retailSpaces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
};

export default RetailSpaceCalendarView;
